(function () {
  "use strict";

  if (customElements.get("phases-testimonial-autoplay")) return;

  customElements.define(
    "phases-testimonial-autoplay",
    class PhasesTestimonialAutoplay extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector("[data-grid-slider]");
        this.gridSlider = this.querySelector("grid-slider");
        if (!this.slider || !this.hasAttribute("loop")) return;

        this.speed = (parseInt(this.getAttribute("data-speed"), 10) || 5) * 1000;
        this.slidesPerTick = parseInt(this.getAttribute("data-slides-per-tick"), 10) || 1;
        this.isPaused = false;
        this.isTicking = false;
        this.isResetting = false;
        this.originalWidth = 0;
        this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.onScroll = this.handleScroll.bind(this);
        this.onMouseEnter = () => {
          this.isPaused = true;
        };
        this.onMouseLeave = () => {
          this.isPaused = false;
        };
        this.onVisibilityChange = () => {
          this.isPaused = document.hidden;
        };
        this.onArrowClick = this.handleArrowClick.bind(this);
        this.keepArrowsEnabled = this.keepArrowsEnabled.bind(this);

        this.initLoop();
        this.initArrows();
        this.slider.addEventListener("scroll", this.onScroll, { passive: true });
        this.addEventListener("mouseenter", this.onMouseEnter);
        this.addEventListener("mouseleave", this.onMouseLeave);
        document.addEventListener("visibilitychange", this.onVisibilityChange);

        if (this.hasAttribute("autoplay") && !this.reducedMotion) {
          this.startAutoplay();
        }
      }

      disconnectedCallback() {
        this.stopAutoplay();
        window.clearTimeout(this.arrowResetTimer);
        window.clearTimeout(this.scrollSettleTimer);
        this.slider?.removeEventListener("scroll", this.onScroll);
        this.gridSlider?.removeEventListener("click", this.onArrowClick, true);
        this.removeEventListener("mouseenter", this.onMouseEnter);
        this.removeEventListener("mouseleave", this.onMouseLeave);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
        document.removeEventListener("theme:resize:width", this.onResizeArrows);
        this.arrowsObserver?.disconnect();
      }

      isMobileSlider() {
        return window.matchMedia("(max-width: 749px)").matches;
      }

      ensureMobileArrows() {
        if (!this.gridSlider || !this.isMobileSlider()) return;

        if (
          this.gridSlider.querySelector("[data-button-prev]") &&
          this.gridSlider.querySelector("[data-button-next]")
        ) {
          return;
        }

        if (this.gridSlider.querySelector("[data-phases-ts-arrows]")) return;

        const wrap = document.createElement("div");
        wrap.classList.add("slider__arrows", "phases-testimonial-slider__arrows");
        wrap.setAttribute("data-phases-ts-arrows", "");

        const themeArrows = window.theme?.sliderArrows;
        if (themeArrows?.prev && themeArrows?.next) {
          wrap.innerHTML = themeArrows.prev + themeArrows.next;
        } else {
          wrap.innerHTML =
            '<button type="button" class="slider__button slider__button--prev" data-button-arrow data-button-prev></button>' +
            '<button type="button" class="slider__button slider__button--next" data-button-arrow data-button-next></button>';
        }

        this.gridSlider.append(wrap);
      }

      initArrows() {
        if (!this.gridSlider) return;

        const setup = () => {
          this.ensureMobileArrows();

          this.buttonPrev = this.gridSlider.querySelector("[data-button-prev]");
          this.buttonNext = this.gridSlider.querySelector("[data-button-next]");
          if (!this.buttonPrev || !this.buttonNext) return false;

          if (!this.arrowsClickBound) {
            this.gridSlider.addEventListener("click", this.onArrowClick, true);
            this.arrowsClickBound = true;
          }

          this.keepArrowsEnabled();

          if (!this.arrowsObserver) {
            this.arrowsObserver = new MutationObserver(this.keepArrowsEnabled);
            this.arrowsObserver.observe(this.buttonPrev, { attributes: true, attributeFilter: ["disabled"] });
            this.arrowsObserver.observe(this.buttonNext, { attributes: true, attributeFilter: ["disabled"] });
          }

          return true;
        };

        if (setup()) return;

        const observer = new MutationObserver(() => {
          if (setup()) observer.disconnect();
        });

        observer.observe(this.gridSlider, { childList: true, subtree: true });

        [0, 100, 400].forEach((delay) => {
          window.setTimeout(() => {
            setup();
            if (delay === 400) observer.disconnect();
          }, delay);
        });

        this.onResizeArrows = () => {
          window.setTimeout(() => setup(), 50);
        };
        document.addEventListener("theme:resize:width", this.onResizeArrows);
      }

      keepArrowsEnabled() {
        if (this.buttonPrev) this.buttonPrev.disabled = false;
        if (this.buttonNext) this.buttonNext.disabled = false;
      }

      handleArrowClick(event) {
        const button = event.target.closest("[data-button-arrow]");
        if (!button || !this.gridSlider?.contains(button)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (button.hasAttribute("data-button-prev")) {
          this.goToAdjacent(-1);
        }

        if (button.hasAttribute("data-button-next")) {
          this.goToAdjacent(1);
        }
      }

      getAllItems() {
        return Array.from(this.slider.querySelectorAll("[data-grid-item]"));
      }

      getOriginalItems() {
        return Array.from(this.slider.querySelectorAll("[data-grid-item]:not([data-clone])"));
      }

      getVisibleItemIndex(items) {
        const sliderRect = this.slider.getBoundingClientRect();
        let visibleIndex = 0;

        for (let index = 0; index < items.length; index += 1) {
          const rect = items[index].getBoundingClientRect();
          if (rect.left >= sliderRect.left - 1) {
            visibleIndex = index;
            break;
          }
          visibleIndex = index;
        }

        return visibleIndex;
      }

      getLastPrependedIndex() {
        return this.getOriginalItems().length - 1;
      }

      goToAdjacent(direction) {
        const items = this.getAllItems();
        if (!items.length || !this.originalWidth) return;

        const gap = this.getGap();
        const visibleIndex = this.getVisibleItemIndex(items);
        let targetLeft = this.slider.scrollLeft;

        if (direction > 0) {
          if (visibleIndex + 1 < items.length) {
            targetLeft = items[visibleIndex + 1].offsetLeft;
          } else {
            targetLeft += items[visibleIndex].offsetWidth + gap;
          }
        } else if (this.slider.scrollLeft <= 1) {
          targetLeft = items[this.getLastPrependedIndex()].offsetLeft;
        } else if (visibleIndex > 0) {
          targetLeft = items[visibleIndex - 1].offsetLeft;
        } else {
          targetLeft = items[this.getLastPrependedIndex()].offsetLeft;
        }

        this.slider.scrollTo({
          left: targetLeft,
          behavior: "smooth",
        });

        window.clearTimeout(this.arrowResetTimer);
        this.arrowResetTimer = window.setTimeout(() => this.finalizeScroll(), 500);
      }

      finalizeScroll() {
        this.handleScroll();
        this.keepArrowsEnabled();
      }

      cloneItem(item, position) {
        const clone = item.cloneNode(true);
        clone.setAttribute("data-clone", position);
        clone.querySelectorAll("[data-aos]").forEach((node) => node.removeAttribute("data-aos"));
        clone.removeAttribute("data-shopify-editor-block");
        return clone;
      }

      initLoop() {
        const items = this.getOriginalItems();
        if (!items.length) return;

        this.slider.querySelectorAll("[data-grid-item][data-clone]").forEach((node) => node.remove());

        const endFragment = document.createDocumentFragment();
        const startFragment = document.createDocumentFragment();

        items.forEach((item) => {
          endFragment.appendChild(this.cloneItem(item, "end"));
          startFragment.appendChild(this.cloneItem(item, "start"));
        });

        this.slider.appendChild(endFragment);
        this.slider.insertBefore(startFragment, this.slider.firstChild);

        const setStartPosition = () => {
          const width = this.getOriginalSetWidth();
          if (!width) return;

          this.originalWidth = width;
          this.isResetting = true;
          this.slider.scrollLeft = width;
          this.isResetting = false;
        };

        requestAnimationFrame(setStartPosition);
        window.setTimeout(setStartPosition, 150);
      }

      getOriginalSetWidth() {
        const items = this.getOriginalItems();
        if (!items.length) return 0;

        const gap = this.getGap();
        const firstItem = items[0];
        const lastItem = items[items.length - 1];

        return lastItem.offsetLeft + lastItem.offsetWidth + gap - firstItem.offsetLeft;
      }

      getGap() {
        const styles = getComputedStyle(this.slider);
        return parseFloat(styles.columnGap || styles.gap) || 0;
      }

      getScrollStep() {
        const items = this.getAllItems();
        if (!items.length) return 0;

        const gap = this.getGap();
        const startIndex = this.getVisibleItemIndex(items);
        let distance = 0;

        for (let index = 0; index < this.slidesPerTick; index += 1) {
          const item = items[startIndex + index];
          if (!item) break;
          distance += item.offsetWidth;
          if (index < this.slidesPerTick - 1) distance += gap;
        }

        return distance;
      }

      handleScroll() {
        if (!this.originalWidth || this.isResetting) return;

        // QA: Chrome shakes the slider on a raw touch/trackpad swipe. Elastic
        // overscroll at the loop boundary fires several `scroll` events while
        // it rubber-bands back, and jumping `scrollLeft` on every one of them
        // fights that bounce mid-gesture. Waiting for the scroll to settle —
        // and forcing the jump itself to be instant, ignoring any inherited
        // `scroll-behavior: smooth` — means we only correct once, after the
        // gesture (bounce included) has actually finished.
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = window.setTimeout(() => this.correctLoopBoundary(), 120);
      }

      correctLoopBoundary() {
        if (!this.originalWidth || this.isResetting) return;

        const width = this.originalWidth;
        const scrollLeft = this.slider.scrollLeft;
        const items = this.getAllItems();

        if (scrollLeft < width * 2 && scrollLeft > 0) return;

        const previousScrollBehavior = this.slider.style.scrollBehavior;
        this.isResetting = true;
        this.slider.style.scrollBehavior = "auto";

        if (scrollLeft >= width * 2) {
          this.slider.scrollLeft = scrollLeft - width;
        } else {
          this.slider.scrollLeft = items[this.getLastPrependedIndex()]?.offsetLeft ?? width;
        }

        this.slider.style.scrollBehavior = previousScrollBehavior;
        this.isResetting = false;
      }

      tick() {
        if (this.isPaused || this.isTicking) return;

        const step = this.getScrollStep();
        if (!step) return;

        if (!this.originalWidth) {
          this.originalWidth = this.getOriginalSetWidth();
        }

        this.isTicking = true;

        this.slider.scrollTo({
          left: this.slider.scrollLeft + step,
          behavior: "smooth",
        });

        window.setTimeout(() => {
          this.finalizeScroll();
          this.isTicking = false;
        }, 500);
      }

      startAutoplay() {
        this.stopAutoplay();
        this.timer = window.setInterval(() => this.tick(), this.speed);
      }

      stopAutoplay() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }
    },
  );
})();
